import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { LogoBackdrop } from "@freightsimple/ui-core/Display/LogoBackdrop";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextArea } from "@freightsimple/ui-core/Controls/TextArea";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { displayNameForCarrier } from "@/Data/displayNameForCarrier";
import type { Quote } from "@freightsimple/generated-dashboard-openapi-client/models/Quote";
import type { ReactNode } from "react";
import { useRef, useState } from "react";

const ReasonIdentifier = {
  ShipmentNotHappening: 0,
  BetterRateElsewhere: 1,
  ComparingWithPastRates: 2,
  Other: 3,
} as const;
type ReasonIdentifier =
  (typeof ReasonIdentifier)[keyof typeof ReasonIdentifier];

// ReasonIdentifier is a numeric enum; stringify for the Dropdown value
// space (the Dropdown API is string-keyed) and parse back on change.
const reasonOptions = [
  {
    value: String(ReasonIdentifier.ShipmentNotHappening),
    label: "Shipment isn't happening",
  },
  {
    value: String(ReasonIdentifier.BetterRateElsewhere),
    label: "A better rate was found elsewhere",
  },
  {
    value: String(ReasonIdentifier.ComparingWithPastRates),
    label: "I was comparing rates against a shipment I made in the past",
  },
  {
    value: String(ReasonIdentifier.Other),
    label: "Other",
  },
];

interface BetterRatesInfoProps {
  onChange: (_: string) => void;
  cheapestQuote: Quote | undefined;
}

function BetterRatesInfo_(props: BetterRatesInfoProps) {
  const [carrierName, setCarrierName] = useState("");
  const [rate, setRate] = useState("");
  const [note, setNote] = useState("");

  function describe(cn: string, r: string, n: string) {
    return [cn, r, n].filter((p) => p !== "").join(", ");
  }

  const { cheapestQuote } = props;

  return (
    // A titleless Panel is ui-core's plain bordered card, which is what the
    // hand-rolled border + shadow was.
    <Panel>
      <Stack gap={4} align="start">
        <Row gap={6} align="start">
          <Stack gap={2} align="start">
            {cheapestQuote !== undefined && (
              <T size="sm">
                FreightSimple found rates from{" "}
                <strong>${cheapestQuote.price}</strong> with{" "}
                <strong>
                  {displayNameForCarrier(cheapestQuote.carrierIdentifier)}
                </strong>
                .
              </T>
            )}
            {props.cheapestQuote === undefined && (
              <T size="sm">
                We&apos;re sorry that FreightSimple could not find a rate for
                this shipment
              </T>
            )}
            <T size="sm">
              Please tell us more about the rate you ended up booking
            </T>
          </Stack>
          {cheapestQuote !== undefined && (
            <LogoBackdrop>
              <img
                src={`/carrierLogosSquare/${cheapestQuote?.carrierIdentifier}.png`}
                alt={`${displayNameForCarrier(cheapestQuote.carrierIdentifier)} logo`}
              />
            </LogoBackdrop>
          )}
        </Row>

        <FormItem label="Carrier / Broker used">
          <TextInput
            value={carrierName}
            onChange={function (e) {
              setCarrierName(e.target.value);
              props.onChange(describe(e.target.value, rate, note));
            }}
          />
        </FormItem>
        <FormItem label="Rate (in $ CAD / USD)">
          <TextInput
            value={rate}
            onChange={function (e) {
              setRate(e.target.value);
              props.onChange(describe(carrierName, e.target.value, note));
            }}
          />
        </FormItem>
        <FormItem label="Any other notes?">
          <TextInput
            value={note}
            onChange={function (e) {
              setNote(e.target.value);
              props.onChange(describe(carrierName, rate, e.target.value));
            }}
          />
        </FormItem>
      </Stack>
    </Panel>
  );
}

interface ViewShipmentsDeleteQuotedShipmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cheapestQuote: Quote | undefined;
  onConfirm: (deletionReason: string) => void;
}

// Delete-a-quoted-shipment dialog: asks why, with extra detail fields for
// some reasons; Confirm stays disabled until a reason is picked.
function ViewShipmentsDeleteQuotedShipmentDialog_(
  props: ViewShipmentsDeleteQuotedShipmentDialogProps,
) {
  const [reasonIdentifier, setReasonIdentifier] = useState<
    undefined | ReasonIdentifier
  >();

  const [moreInfo, setMoreInfo] = useState<string>("");

  const noReasonYet = reasonIdentifier === undefined;

  function showMoreInfo() {
    return (
      reasonIdentifier !== undefined &&
      reasonIdentifier !== ReasonIdentifier.ShipmentNotHappening
    );
  }

  function reasonText() {
    const text = reasonOptions.find(
      (o) => o.value === String(reasonIdentifier),
    );

    if (text === undefined) {
      throw new Error("Should not get here");
    }

    if (showMoreInfo()) {
      return text.label + ":" + moreInfo;
    } else {
      return text.label;
    }
  }

  function placeholderForReason(reason: ReasonIdentifier | undefined): string {
    if (reason === ReasonIdentifier.Other) {
      return "Your feedback helps us make FreightSimple better for you. Please leave your comments in this box!";
    }

    if (reason === ReasonIdentifier.BetterRateElsewhere) {
      return "Help us improve FreightSimple by letting us know if there are carriers or rates you would like to see available in FreightSimple";
    }

    if (reason === ReasonIdentifier.ComparingWithPastRates) {
      return "Help us improve FreightSimple by letting us know if we are doing better or worse than other providers you have used in the past";
    }

    return "";
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Delete shipment"
      description="You can delete this shipment and the associated quotes if you no longer need to book it. Please help us improve FreightSimple by letting us know why you're deleting it"
      footer={
        <>
          <Button onClick={() => props.onOpenChange(false)}>Cancel</Button>
          <Button
            primary
            disabled={noReasonYet}
            disabledMessage="Provide a reason for deletion to continue"
            onClick={function () {
              props.onConfirm(reasonText());
              props.onOpenChange(false);
            }}
          >
            Confirm
          </Button>
        </>
      }
    >
      <ControlPresentationProvider presentation="underlined">
        {/* The body used to cap itself at an arbitrary 600px; the width now
            comes from the Dialog's own scale. */}
        <Stack gap={2} align="start">
          <Dropdown
            placeholder="Select a reason"
            value={
              reasonIdentifier !== undefined
                ? String(reasonIdentifier)
                : undefined
            }
            onChange={(v) => setReasonIdentifier(Number(v) as ReasonIdentifier)}
            options={reasonOptions}
          />
          {reasonIdentifier === ReasonIdentifier.BetterRateElsewhere && (
            <BetterRatesInfo_
              onChange={function (newValue) {
                setMoreInfo(newValue);
              }}
              cheapestQuote={props.cheapestQuote}
            />
          )}
          {(reasonIdentifier === ReasonIdentifier.Other ||
            reasonIdentifier === ReasonIdentifier.ComparingWithPastRates) && (
            <TextArea
              rows={4}
              value={moreInfo}
              onChange={(e) => setMoreInfo(e.currentTarget.value)}
              placeholder={placeholderForReason(reasonIdentifier)}
            />
          )}
        </Stack>
      </ControlPresentationProvider>
    </Dialog>
  );
}

// Awaitable wrapper matching the legacy useDeleteQuotedShipmentDialog
// contract: resolves the deletion reason, or undefined on cancel/close.
export function useDeleteQuotedShipmentDialog(): {
  show: (cheapestQuote: Quote | undefined) => Promise<string | undefined>;
  dialogs: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [cheapestQuote, setCheapestQuote] = useState<Quote | undefined>(
    undefined,
  );
  const resolverRef = useRef<
    ((deletionReason: string | undefined) => void) | undefined
  >(undefined);

  function show(quote: Quote | undefined): Promise<string | undefined> {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setCheapestQuote(quote);
      setOpenCount((count) => count + 1);
      setOpen(true);
    });
  }

  function settle(deletionReason: string | undefined) {
    resolverRef.current?.(deletionReason);
    resolverRef.current = undefined;
  }

  const dialogs = (
    <ViewShipmentsDeleteQuotedShipmentDialog_
      key={`view-shipments-delete-quoted-${openCount}`}
      open={open}
      onOpenChange={function (nowOpen) {
        setOpen(nowOpen);
        if (!nowOpen) {
          settle(undefined);
        }
      }}
      cheapestQuote={cheapestQuote}
      onConfirm={settle}
    />
  );

  return { show, dialogs };
}
